import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShopContext } from './ShopContext';

export const ShoppingCart = ({ isOpen, onClose }) => {
  const shopContext = useShopContext();
  const [cart, setCart] = React.useState([]);
  const [totalPrice, setTotalPrice] = React.useState(0);
  const navigate = useNavigate();

  // Update local state from context
  useEffect(() => {
    if (shopContext) {
      setCart(shopContext.cart || []);
      setTotalPrice(shopContext.totalPrice || 0);
    }
  }, [shopContext]);

  const handleCheckout = () => {
    console.log("Navigating to checkout with cart:", cart);
    if (cart.length === 0) {
      showToast("Jūsu grozs ir tukšs. Pievienojiet preci, lai turpinātu.", "warning");
      return;
    }
    onClose();
    navigate('/shop/checkout');
  };

  const handleRemoveItem = (item) => {
    if (shopContext && typeof shopContext.removeFromCart === 'function') {
      shopContext.removeFromCart(item.id);
      // Show toast message (using our custom implementation)
      showToast(`${item.name} removed from cart`);
    }
  };

  const handleClearCart = () => {
    if (shopContext && typeof shopContext.clearCart === 'function') {
      shopContext.clearCart();
      // Show toast message
      showToast('Iztukšots!');
    }
  };

  const handleQuantityChange = (productId, value) => {
    if (shopContext && typeof shopContext.updateQuantity === 'function') {
      shopContext.updateQuantity(productId, value);
    }
  };

  // Custom simple toast notification system
  const showToast = (message, type = 'success') => {
    const toast = document.createElement('div');
    toast.className = `fixed top-24 left-1/2 transform -translate-x-1/2 z-50 px-4 py-2 rounded-md shadow-lg ${
      type === 'success' ? 'bg-green-500' : 
      type === 'warning' ? 'bg-yellow-500' : 
      type === 'info' ? 'bg-blue-500' : 'bg-red-500'
    } text-white`;
    toast.textContent = message;
    document.body.appendChild(toast);
    
    setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transition = 'opacity 0.5s ease';
      
      setTimeout(() => {
        document.body.removeChild(toast);
      }, 500);
    }, 3000);
  };

  return (
    <>
      {/* Semi-transparent overlay that still allows content visibility */}
      <div 
        className={`fixed inset-0 transition-opacity duration-300 ease-in-out z-40 ${
          isOpen ? 'bg-opacity-50 backdrop-blur-sm' : 'bg-opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      ></div>

      {/* Drawer/Sidebar with slide animation */}
      <div 
        className={`fixed right-0 top-0 h-full w-80 bg-gray-900 z-50 shadow-xl transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-gray-800 border-b border-gray-700">
          <h2 className="text-white font-semibold text-lg">Grozs</h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white focus:outline-none transition-colors duration-200"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto h-[calc(100%-10rem)] text-white">
          {cart.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <p className="text-gray-300 mb-2">Jūsu grozs ir tukšs</p>
              <p className="text-gray-400 text-sm">Pievienojiet preci, lai turpinātu.</p>
            </div>
          ) : (
            <div className="py-2">
              {cart.map(item => (
                <div key={item.id} className="px-4 py-3 border-b border-gray-700 flex space-x-3">
                  <div className="w-16 h-16 bg-gray-800 rounded overflow-hidden flex-shrink-0">
                    {item.image ? (
                      <img 
                        src={item.image} 
                        alt={item.name} 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/64?text=NT';
                          e.target.onerror = null;
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-500">
                        <span>No img</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h4 className="text-white font-medium mb-1 truncate">{item.name}</h4>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-green-400 font-bold">
                        €{(item.finalPrice || item.price).toFixed(2)}
                      </span>
                      
                      <button
                        onClick={() => handleRemoveItem(item)}
                        className="text-gray-400 hover:text-red-500 focus:outline-none transition-colors duration-200"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                    
                    {/* Display selected options */}
                    {(item.selectedWidth || item.selectedLength) && (
                      <div className="text-xs text-gray-400 mb-2">
                        {item.selectedWidth && <span>Platums: {item.selectedWidth}m</span>}
                        {item.selectedWidth && item.selectedLength && <span> • </span>}
                        {item.selectedLength && <span>Garums: {item.selectedLength}m</span>}
                      </div>
                    )}
                    
                    <div className="flex items-center">
                      <span className="text-gray-400 mr-2 text-sm">Skaits:</span>
                      <div className="flex items-center">
                        <button 
                          onClick={() => handleQuantityChange(item.id, Math.max(1, item.quantity - 1))}
                          className="bg-gray-700 text-white w-6 h-6 flex items-center justify-center rounded-l-sm hover:bg-gray-600 transition-colors duration-200"
                        >
                          -
                        </button>
                        <input
                          type="number"
                          min="1"
                          max={item.stock || 99}
                          value={item.quantity}
                          onChange={(e) => handleQuantityChange(item.id, parseInt(e.target.value) || 1)}
                          className="w-6 bg-gray-800 border-0 text-center text-white h-6 focus:outline-none [-moz-appearance:_textfield] [&::-webkit-outer-spin-button]:m-0 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <button 
                          onClick={() => handleQuantityChange(item.id, Math.min(item.stock || 99, item.quantity + 1))}
                          className="bg-gray-700 text-white w-6 h-6 flex items-center justify-center rounded-r-sm hover:bg-gray-600 transition-colors duration-200"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-gray-700 bg-gray-900 p-4">
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-t border-gray-700">
              <span className="text-gray-300 font-semibold">Kopā:</span>
              <span className="text-white font-bold">€{totalPrice.toFixed(2)}</span>
            </div>
            
            <button 
              onClick={handleCheckout}
              disabled={cart.length === 0}
              className={`w-full py-3 px-4 rounded-md font-semibold transition-all duration-200 flex items-center justify-center shadow-md ${
                cart.length > 0
                  ? 'bg-green-400 hover:bg-green-500 active:bg-green-600 text-black transform hover:scale-[1.02] active:scale-[0.98] hover:shadow-lg' 
                  : 'bg-gray-700 text-gray-400 cursor-not-allowed'
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Sūtījuma Noformēšana
            </button>
            
            {cart.length > 0 && (
              <button 
                onClick={handleClearCart}
                className="w-full py-2 px-4 rounded-md text-gray-400 hover:text-white hover:bg-gray-800 transition-colors duration-200"
              >
                Iztukšot grozu
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}; 